/**
 * DocumentStatusBadge - Status indicator for document/folder compliance
 *
 * Displays visual status badges matching the DocumentComplianceEngine statuses.
 * Used in VehicleDocumentsTab and other document views.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Minus,
  FileText,
  Slash
} from 'lucide-react';

// Status configuration matching DocumentComplianceEngine
// NB: pas de `label` ici — un libellé figé dans une constante de module ne suit pas
// le changement de langue (piège i18n #1). Le libellé se résout au rendu via
// STATUS_LABEL_KEYS ci-dessous.
const STATUS_CONFIG = {
  complete: {
    icon: CheckCircle,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    iconColor: 'text-green-600'
  },
  missing: {
    icon: AlertTriangle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    iconColor: 'text-red-600'
  },
  expiring: {
    icon: Clock,
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-700',
    iconColor: 'text-orange-600'
  },
  expired: {
    icon: XCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    iconColor: 'text-red-600'
  },
  empty: {
    icon: Minus,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-600',
    iconColor: 'text-gray-500'
  },
  has_docs: {
    icon: FileText,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    iconColor: 'text-blue-600'
  },
  not_applicable: {
    icon: Slash,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-500',
    iconColor: 'text-gray-400'
  },
  pending_validation: {
    icon: Clock,
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    iconColor: 'text-amber-600'
  },
  validation_rejected: {
    icon: XCircle,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    iconColor: 'text-red-600'
  },
  exception_recorded: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-700',
    iconColor: 'text-yellow-600'
  }
};

// Clé i18n par code de statut — réutilise `documents.*` quand le texte affiché
// correspond exactement, sinon clé propre sous `common.docs.status`.
const STATUS_LABEL_KEYS = {
  complete: 'documents.folderStatus.complete',
  missing: 'documents.folderStatus.missing',
  expiring: 'documents.folderStatus.expiring',
  expired: 'documents.folderStatus.expired',
  empty: 'common.docs.status.optional',
  has_docs: 'common.docs.status.hasDocs',
  not_applicable: 'common.states.notAvailable',
  pending_validation: 'common.docs.status.pendingShort',
  validation_rejected: 'documents.validationStatus.rejected',
  exception_recorded: 'common.docs.status.exception'
};

/**
 * DocumentStatusBadge
 *
 * @param {Object} props
 * @param {string} props.status - Status code (complete, missing, expiring, etc.)
 * @param {number} [props.expiresIn] - Days until expiry (for expiring status)
 * @param {boolean} [props.showLabel=true] - Whether to show text label
 * @param {boolean} [props.compact=false] - Compact mode (icon only)
 * @param {string} [props.className] - Additional CSS classes
 */
function DocumentStatusBadge({
  status,
  expiresIn,
  showLabel = true,
  compact = false,
  className = ''
}) {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.empty;
  const Icon = config.icon;
  const labelKey = STATUS_LABEL_KEYS[status] || STATUS_LABEL_KEYS.empty;

  // Build label with days if expiring
  let displayLabel = t(labelKey);
  if (status === 'expiring' && expiresIn !== undefined) {
    displayLabel = t('common.docs.status.daysShort', { days: expiresIn });
  }

  if (compact) {
    return (
      <span
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${config.bgColor} ${className}`}
        title={displayLabel}
      >
        <Icon className={`w-3 h-3 ${config.iconColor}`} />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor} ${className}`}
    >
      <Icon className={`w-3 h-3 ${config.iconColor}`} />
      {showLabel && <span>{displayLabel}</span>}
    </span>
  );
}

/**
 * DocumentValidationBadge - Specific badge for document validation status
 *
 * @param {Object} props
 * @param {string} props.validationStatus - validated, pending, rejected, cannot_provide
 */
export function DocumentValidationBadge({ validationStatus, className = '' }) {
  const statusMap = {
    validated: 'complete',
    pending: 'pending_validation',
    rejected: 'validation_rejected',
    cannot_provide: 'exception_recorded'
  };

  const mappedStatus = statusMap[validationStatus] || 'empty';
  return <DocumentStatusBadge status={mappedStatus} className={className} />;
}

export default DocumentStatusBadge;
